/**
 * Subscription RPC definitions
 *
 * Authenticated endpoints for subscription management.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Subscription } from "@fm/db/frontend";
import { Schema } from "effect";
import { AuthMiddleware } from "../auth.tags.ts";
import { BrandMiddleware } from "../brand.tags.ts";
import { DatabaseError, Forbidden, SubscriptionError, Unauthorized } from "../errors.ts";

/**
 * Optional org scope for the caller's subscription reads. When `customerId` is
 * present it only SELECTS among the caller's memberships (a non-member id is
 * {@link Forbidden}); when absent the default org is resolved server-side
 * (subscription-bearing, else first membership). Omitting the field is
 * back-compatible with the pre-multi-org single-org behavior.
 *
 * @since 1.0.0
 */
export const SubscriptionScopeInput = Schema.Struct({
  customerId: Schema.optional(Schema.NonEmptyTrimmedString),
});
export type SubscriptionScopeInput = Schema.Schema.Type<typeof SubscriptionScopeInput>;

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Create checkout session request
 * @since 1.0.0
 */
const CreateCheckoutSessionRequest = Schema.Struct({
  priceId: Schema.String,
  successUrl: Schema.String,
  cancelUrl: Schema.String,
});

/**
 * Create checkout session response
 * @since 1.0.0
 */
const CreateCheckoutSessionResponse = Schema.Struct({
  sessionId: Schema.String,
  url: Schema.String,
});

/**
 * Create portal session request
 * @since 1.0.0
 */
const CreatePortalSessionRequest = Schema.Struct({
  returnUrl: Schema.String,
  // Optional org scope — selects among the caller's memberships (a non-member id
  // is Forbidden and NO portal is opened); omitted resolves the default org.
  customerId: Schema.optional(Schema.NonEmptyTrimmedString),
});

/**
 * Create portal session response
 * @since 1.0.0
 */
const CreatePortalSessionResponse = Schema.Struct({
  url: Schema.String,
});

/**
 * Subscription response - nullable since user may not have subscription
 * @since 1.0.0
 */
const SubscriptionResponse = Schema.NullOr(Subscription.json);

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Subscription RPC group containing subscription-related endpoints
 *
 * @since 1.0.0
 */
export class SubscriptionRpcs extends RpcGroup.make(
  /**
   * Find current user's subscription
   * Returns null if user has no subscription
   */
  Rpc.make("findSubscription", {
    payload: SubscriptionScopeInput,
    success: SubscriptionResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  /**
   * Create a Stripe Checkout session for subscription purchase
   * Returns the session URL for redirect
   */
  Rpc.make("createCheckoutSession", {
    payload: CreateCheckoutSessionRequest,
    success: CreateCheckoutSessionResponse,
    error: Schema.Union(Unauthorized, SubscriptionError, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  /**
   * Create a Stripe Customer Portal session for subscription management
   * Returns the portal URL for redirect
   */
  Rpc.make("createPortalSession", {
    payload: CreatePortalSessionRequest,
    success: CreatePortalSessionResponse,
    error: Schema.Union(Unauthorized, Forbidden, SubscriptionError, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  /**
   * Cancel subscription at period end
   */
  Rpc.make("cancelSubscription", {
    success: Schema.Struct({ success: Schema.Boolean }),
    error: Schema.Union(Unauthorized, SubscriptionError, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  /**
   * Resume a cancelled subscription (before period end)
   */
  Rpc.make("resumeSubscription", {
    success: Schema.Struct({ success: Schema.Boolean }),
    error: Schema.Union(Unauthorized, SubscriptionError, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
) {}
